import moment from 'moment';
import {
	buildCalendarMonthRange,
	buildCalendarYearRange,
	deriveMonthIndicesSpanningRange,
	deriveYearsIncludedInRange,
	formatStatisticsMultiYearRangeLabel,
	matchCalendarMonth,
	matchCalendarQuarter,
	matchCalendarYear,
	resolveStatisticsPeriodDisplayLabel,
	STATISTICS_QUICK_PICK_MONTHS,
	STATISTICS_QUICK_PICK_QUARTERS,
	formatMonthIndicesRangeLabel,
} from './statisticsPeriodQuickPickUtils';
import { normalizeCompareDateKey } from './statisticsCompareConfig';
import { TREND_MONTHLY_YEARS_MAX } from './trendMonthlyConstants';

export { TREND_MONTHLY_YEARS_MIN, TREND_MONTHLY_YEARS_MAX } from './trendMonthlyConstants';
export { deriveYearsIncludedInRange } from './statisticsPeriodQuickPickUtils';

/** 锚点折线 X 轴：按月各点 vs 按季汇总 */
export const PERIOD_QUICK_PICK_AXIS = {
	MONTH: 'month',
	QUARTER: 'quarter',
};

export const FULL_YEAR_MONTH_INDICES = STATISTICS_QUICK_PICK_MONTHS.map(({ month }) => month);
export const FULL_QUARTER_INDICES = STATISTICS_QUICK_PICK_QUARTERS.map(({ quarter }) => quarter);

export const isDualFullYearQuarterAnchor = (anchor) => anchor?.months?.length === FULL_YEAR_MONTH_INDICES.length && anchor?.quarters?.length === FULL_QUARTER_INDICES.length;

export const isFullYearPeriodSelection = (months = [], quarters = []) => months.length === FULL_YEAR_MONTH_INDICES.length && quarters.length === FULL_QUARTER_INDICES.length;

const formatMultiYearQuickPickLabel = (years = [], panelYear, panelYearRange = null) => {
	if (!years.length) return panelYearRange?.label ?? '';
	if (years.length === 1) return panelYearRange?.label ?? `${years[0]}年`;
	if (panelYearRange?.label && years.length === 1) return panelYearRange.label;
	if ((panelYearRange?.months?.length ?? 0) > 0 && years.includes(panelYear)) {
		const otherYears = years.filter((year) => year !== panelYear);
		if (otherYears.length) {
			return `${formatStatisticsMultiYearRangeLabel(otherYears)} · ${withYearPrefix(panelYear, panelYearRange.label)}`;
		}
	}
	return formatStatisticsMultiYearRangeLabel(years);
};

/** 整年 + 展示方式：2024年 · 1~12月 / 2024年 · 各季 */
export const formatFullYearPeriodAxisLabel = (year, periodAxis = PERIOD_QUICK_PICK_AXIS.MONTH) => {
	if (periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER) {
		const suffix = '各季';
		return Number.isFinite(Number(year)) ? `${Number(year)}年 · ${suffix}` : suffix;
	}
	const monthLabel = formatMonthIndicesRangeLabel(FULL_YEAR_MONTH_INDICES);
	return Number.isFinite(Number(year)) ? `${Number(year)}年 · ${monthLabel}` : monthLabel;
};

export const createEmptyPeriodQuickPickStaging = () => ({
	months: [],
	quarters: [],
	years: [],
	yearPeriods: {},
	periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
});

const normalizeYearPeriodEntry = (entry = {}) =>
	normalizePeriodQuickPickStaging({
		months: entry.months ?? [],
		quarters: entry.quarters ?? [],
		periodAxis: entry.periodAxis ?? PERIOD_QUICK_PICK_AXIS.MONTH,
	});

/** 下单/完成日期：读取某年的月/季细选（无 entry = 整年） */
export const getOrderRangeYearPeriodView = (staging = {}, editYear) => {
	const entry = staging.yearPeriods?.[Number(editYear)];
	if (!entry) {
		return normalizePeriodQuickPickStaging({ months: [], quarters: [], periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH });
	}
	return normalizeYearPeriodEntry(entry);
};

const persistOrderRangeYearPeriodView = (staging, editYear, yearPeriodView) => {
	const normalized = normalizeYearPeriodEntry(yearPeriodView);
	const yearPeriods = { ...(staging.yearPeriods ?? {}) };
	const yearKey = Number(editYear);
	const months = normalized.months ?? [];

	if (!Number.isFinite(yearKey)) {
		return normalizePeriodQuickPickStaging(staging);
	}

	if (!months.length || isFullYearPeriodSelection(months, normalized.quarters ?? [])) {
		delete yearPeriods[yearKey];
	} else {
		yearPeriods[yearKey] = {
			months: normalized.months,
			periodAxis: normalized.periodAxis,
		};
	}

	return normalizePeriodQuickPickStaging({
		...staging,
		yearPeriods,
		months: normalized.months,
		quarters: normalized.quarters,
		periodAxis: normalized.periodAxis,
	});
};

export const clearOrderRangeYearPeriod = (staging, editYear) => {
	const yearPeriods = { ...(staging.yearPeriods ?? {}) };
	delete yearPeriods[Number(editYear)];
	return normalizePeriodQuickPickStaging({
		...staging,
		yearPeriods,
		months: [],
		quarters: [],
		periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
	});
};

export const orderRangeHasYearPeriodOverride = (staging = {}, year) => Boolean(staging.yearPeriods?.[Number(year)]?.months?.length);

export const syncOrderRangeStagingForEditYear = (staging = {}, editYear) => {
	const view = getOrderRangeYearPeriodView(staging, editYear);
	return normalizePeriodQuickPickStaging({
		...staging,
		months: view.months,
		quarters: view.quarters,
		periodAxis: view.periodAxis,
	});
};

export const monthsForQuarter = (quarterIndex) => {
	const startMonth = quarterIndex * 3;
	return [startMonth, startMonth + 1, startMonth + 2];
};

/** 由已选月份反推「整季齐选」的季度下标 */
export const deriveQuartersFromMonths = (months = []) => {
	const monthSet = new Set(months);
	const quarters = [];
	for (const { quarter } of STATISTICS_QUICK_PICK_QUARTERS) {
		if (monthsForQuarter(quarter).every((month) => monthSet.has(month))) {
			quarters.push(quarter);
		}
	}
	return quarters;
};

const isFullYearStaging = (staging = {}) =>
	isFullYearPeriodSelection(staging.months ?? [], deriveQuartersFromMonths(staging.months ?? []));

/** 季度选中：整年时四季可亮；按季选期时只亮季度，不连带点亮月份 */
export const isPeriodQuickPickQuarterSelected = (staging, quarterIndex) => {
	if (!monthsForQuarter(quarterIndex).every((month) => staging.months?.includes(month))) return false;
	if (isFullYearStaging(staging)) return true;
	return staging.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER;
};

export const isPeriodQuickPickMonthSelected = (staging, monthIndex) => {
	if (!staging.months?.includes(monthIndex)) return false;
	if (isFullYearStaging(staging)) return true;
	return staging.periodAxis !== PERIOD_QUICK_PICK_AXIS.QUARTER;
};

/** 季/月互斥：选了季度则月份不可选，选了月份则季度不可选；整年不锁 */
export const resolvePeriodQuickPickChipLock = ({
	staging,
	isMultiConfirm = false,
	panelYear,
	normalizedStart,
	normalizedEnd,
} = {}) => {
	if (isMultiConfirm) {
		const months = staging?.months ?? [];
		if (!months.length || isFullYearStaging(staging)) {
			return { monthsDisabled: false, quartersDisabled: false };
		}
		if (staging.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER) {
			return { monthsDisabled: true, quartersDisabled: false };
		}
		return { monthsDisabled: false, quartersDisabled: true };
	}

	if (!Number.isFinite(Number(panelYear)) || !normalizedStart || !normalizedEnd) {
		return { monthsDisabled: false, quartersDisabled: false };
	}

	const quarterActive = STATISTICS_QUICK_PICK_QUARTERS.some(({ quarter }) =>
		matchCalendarQuarter(normalizedStart, normalizedEnd, panelYear, quarter),
	);
	if (quarterActive) return { monthsDisabled: true, quartersDisabled: false };

	const monthActive = STATISTICS_QUICK_PICK_MONTHS.some(({ month }) =>
		matchCalendarMonth(normalizedStart, normalizedEnd, panelYear, month),
	);
	if (monthActive) return { monthsDisabled: false, quartersDisabled: true };

	return { monthsDisabled: false, quartersDisabled: false };
};

/** 以 months 为基准，同步 quarters（仅用于面板季度 chip 高亮，不决定折线粒度） */
export const normalizePeriodQuickPickStaging = (staging = {}) => {
	const months = [...new Set(staging.months ?? [])].sort((a, b) => a - b);
	const periodAxis = staging.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH;
	return {
		...staging,
		months,
		quarters: deriveQuartersFromMonths(months),
		years: staging.years ?? [],
		yearPeriods: staging.yearPeriods ?? {},
		periodAxis,
	};
};

const periodStagingFromRange = (startDate, endDate, year, preferredPeriodAxis = null) => {
	if (!startDate || !endDate || !Number.isFinite(Number(year))) return null;
	if (matchCalendarYear(startDate, endDate, year)) return null;

	const periodStaging = anchorRangeToPeriodQuickPickStaging(startDate, endDate, year);
	if (!(periodStaging.months?.length ?? 0)) return null;
	if (isFullYearPeriodSelection(periodStaging.months ?? [], periodStaging.quarters ?? [])) return null;

	const periodAxis =
		preferredPeriodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER || preferredPeriodAxis === PERIOD_QUICK_PICK_AXIS.MONTH
			? preferredPeriodAxis
			: (periodStaging.periodAxis ?? PERIOD_QUICK_PICK_AXIS.MONTH);

	return {
		months: periodStaging.months,
		periodAxis,
	};
};

const entryFromSavedOrderRangePeriod = (period = {}, year) => {
	if (!Number.isFinite(Number(year))) return null;

	if (period.orderCreatedMonths?.length) {
		const monthIndices = period.orderCreatedMonths.map((month) => Number(month) - 1).filter((month) => Number.isFinite(month) && month >= 0 && month <= 11);
		if (monthIndices.length) {
			return normalizeYearPeriodEntry({
				months: monthIndices,
				periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
			});
		}
	}

	const savedPeriodAxis = period.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH;

	if (Array.isArray(period.months) && period.months.length) {
		return normalizeYearPeriodEntry({
			months: period.months,
			periodAxis: savedPeriodAxis,
		});
	}

	if (savedPeriodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && Array.isArray(period.quarters) && period.quarters.length) {
		return normalizeYearPeriodEntry({
			months: [...expandQuartersToMonths(period.quarters)],
			periodAxis: PERIOD_QUICK_PICK_AXIS.QUARTER,
		});
	}

	return periodStagingFromRange(period.startDate, period.endDate, year, savedPeriodAxis);
};

const normalizeSavedYearPeriods = (yearPeriods = {}) => {
	if (!yearPeriods || typeof yearPeriods !== 'object') return {};
	const normalized = {};
	for (const [rawYear, entry] of Object.entries(yearPeriods)) {
		const year = Number(rawYear);
		if (!Number.isFinite(year) || !entry) continue;
		normalized[year] = normalizeYearPeriodEntry(entry);
	}
	return normalized;
};

/** 下单/完成日期选期：从已保存 anchor 反填 years + yearPeriods */
export const orderRangeStagingFromSavedAnchor = (startDate, endDate, panelYear, saved = {}) => {
	const empty = createEmptyPeriodQuickPickStaging();
	const savedYearPeriods = normalizeSavedYearPeriods(saved.yearPeriods);

	if (Object.keys(savedYearPeriods).length > 0) {
		const years = (saved.years?.length ? saved.years : Object.keys(savedYearPeriods).map(Number)).filter(Number.isFinite).sort((a, b) => a - b);
		const editYear = years.includes(Number(panelYear)) ? Number(panelYear) : years[years.length - 1];
		return syncOrderRangeStagingForEditYear(
			normalizePeriodQuickPickStaging({
				...empty,
				years,
				yearPeriods: savedYearPeriods,
			}),
			editYear,
		);
	}

	if (Array.isArray(saved.periods) && saved.periods.length > 0) {
		const years = [...new Set(saved.periods.map((period) => moment(period.startDate).year()).filter((year) => Number.isFinite(year)))].sort((a, b) => a - b);
		const yearPeriods = {};
		for (const period of saved.periods) {
			const year = moment(period.startDate).year();
			if (!Number.isFinite(year)) continue;
			const entry = entryFromSavedOrderRangePeriod(period, year);
			if (entry) yearPeriods[year] = entry;
		}
		const editYear = years.includes(Number(panelYear)) ? Number(panelYear) : years[years.length - 1];
		return syncOrderRangeStagingForEditYear(
			normalizePeriodQuickPickStaging({
				...empty,
				years,
				yearPeriods,
			}),
			editYear,
		);
	}

	const savedMonths = Array.isArray(saved.months) ? saved.months : [];
	const savedQuarters = Array.isArray(saved.quarters) ? saved.quarters : [];
	const savedPeriodAxis = saved.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && savedQuarters.length > 0 ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH;

	if (startDate && endDate) {
		const spanYears = deriveYearsIncludedInRange(startDate, endDate);
		if (spanYears.length > 1) {
			const envelopeStart = normalizeCompareDateKey(buildCalendarYearRange(spanYears[0]).startDate);
			const envelopeEnd = normalizeCompareDateKey(buildCalendarYearRange(spanYears[spanYears.length - 1]).endDate);
			const isEnvelopeOnly = envelopeStart === normalizeCompareDateKey(startDate) && envelopeEnd === normalizeCompareDateKey(endDate);
			if (isEnvelopeOnly && !savedMonths.length && !savedQuarters.length) {
				const editYear = spanYears.includes(Number(panelYear)) ? Number(panelYear) : spanYears[spanYears.length - 1];
				return syncOrderRangeStagingForEditYear(
					normalizePeriodQuickPickStaging({
						...empty,
						years: spanYears,
					}),
					editYear,
				);
			}
		}
	}

	const anchorYear = moment(startDate).year();
	if (Number.isFinite(anchorYear) && (savedMonths.length > 0 || savedQuarters.length > 0)) {
		const monthSet = new Set(savedMonths);
		if (savedPeriodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER) {
			for (const quarter of savedQuarters) {
				for (const month of monthsForQuarter(quarter)) {
					monthSet.add(month);
				}
			}
		}
		const yearPeriods = {
			[anchorYear]: normalizeYearPeriodEntry({
				months: [...monthSet],
				periodAxis: savedPeriodAxis,
			}),
		};
		return syncOrderRangeStagingForEditYear(
			normalizePeriodQuickPickStaging({
				...empty,
				years: [anchorYear],
				yearPeriods,
			}),
			anchorYear,
		);
	}

	if (startDate && endDate && Number.isFinite(anchorYear)) {
		const entry = periodStagingFromRange(startDate, endDate, anchorYear);
		if (entry) {
			return syncOrderRangeStagingForEditYear(
				normalizePeriodQuickPickStaging({
					...empty,
					years: [anchorYear],
					yearPeriods: { [anchorYear]: entry },
				}),
				anchorYear,
			);
		}
		if (matchCalendarYear(startDate, endDate, anchorYear)) {
			return syncOrderRangeStagingForEditYear(
				normalizePeriodQuickPickStaging({
					...empty,
					years: [anchorYear],
				}),
				anchorYear,
			);
		}
	}

	return empty;
};

/** 从已生效锚点反填月/季 staging（同一年内） */
export const anchorStagingFromSavedAnchor = (startDate, endDate, panelYear, saved = {}) => {
	if (Array.isArray(saved.periods) && saved.periods.length > 1) {
		const years = [...new Set(saved.periods.map((period) => moment(period.startDate).year()).filter((year) => Number.isFinite(year)))].sort((a, b) => a - b);
		const activeYear = years.includes(panelYear) ? panelYear : (years[years.length - 1] ?? panelYear);
		const activePeriod = saved.periods.find((period) => moment(period.startDate).year() === activeYear) ?? saved.periods[saved.periods.length - 1];
		const base = anchorRangeToPeriodQuickPickStaging(activePeriod?.startDate, activePeriod?.endDate, activeYear);
		if (activePeriod?.orderCreatedMonths?.length) {
			const monthIndices = activePeriod.orderCreatedMonths.map((month) => Number(month) - 1).filter((month) => Number.isFinite(month) && month >= 0 && month <= 11);
			if (monthIndices.length) {
				return normalizePeriodQuickPickStaging({
					...base,
					months: monthIndices,
					years,
					periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
				});
			}
		}
		return normalizePeriodQuickPickStaging({
			...base,
			years,
		});
	}

	const months = Array.isArray(saved.months) ? saved.months : [];
	const quarters = Array.isArray(saved.quarters) ? saved.quarters : [];
	const periodAxis = saved.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && quarters.length > 0 ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH;
	if (months.length > 0 || quarters.length > 0) {
		const monthSet = new Set(months);
		if (periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER) {
			for (const quarter of quarters) {
				for (const month of monthsForQuarter(quarter)) {
					monthSet.add(month);
				}
			}
		}
		return normalizePeriodQuickPickStaging({
			months: [...monthSet],
			quarters: [],
			years: Number.isFinite(Number(panelYear)) ? [Number(panelYear)] : [],
			periodAxis,
		});
	}
	const base = anchorRangeToPeriodQuickPickStaging(startDate, endDate, panelYear);
	const spanYears = deriveYearsIncludedInRange(startDate, endDate);
	if (spanYears.length > 1) {
		return normalizePeriodQuickPickStaging({
			...base,
			years: spanYears,
		});
	}
	return base;
};

export const anchorRangeToPeriodQuickPickStaging = (startDate, endDate, panelYear) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid() || start.year() !== panelYear || end.year() !== panelYear) {
		return createEmptyPeriodQuickPickStaging();
	}

	const quarters = [];
	for (const { quarter } of STATISTICS_QUICK_PICK_QUARTERS) {
		if (matchCalendarQuarter(startDate, endDate, panelYear, quarter)) {
			quarters.push(quarter);
		}
	}
	if (matchCalendarYear(startDate, endDate, panelYear)) {
		return normalizePeriodQuickPickStaging({
			months: deriveMonthIndicesSpanningRange(startDate, endDate),
			quarters: [],
			years: [],
		});
	}

	const months = [];
	for (const { month } of STATISTICS_QUICK_PICK_MONTHS) {
		if (matchCalendarMonth(startDate, endDate, panelYear, month)) {
			months.push(month);
		}
	}
	if (months.length > 0) {
		return normalizePeriodQuickPickStaging({ months, quarters: [], years: [] });
	}

	const spanningMonths = deriveMonthIndicesSpanningRange(startDate, endDate);
	if (spanningMonths.length > 0) {
		return normalizePeriodQuickPickStaging({ months: spanningMonths, quarters: [], years: [] });
	}

	if (quarters.length > 0) {
		const expandedMonths = [...expandQuartersToMonths(quarters)];
		return normalizePeriodQuickPickStaging({
			months: expandedMonths,
			quarters: [],
			years: [],
			periodAxis: PERIOD_QUICK_PICK_AXIS.QUARTER,
		});
	}

	return createEmptyPeriodQuickPickStaging();
};

const expandQuartersToMonths = (quarters = []) => {
	const monthSet = new Set();
	for (const quarter of quarters) {
		for (const month of monthsForQuarter(quarter)) {
			monthSet.add(month);
		}
	}
	return monthSet;
};

/** 从锚点区间反推月份下标（0~11）；不含季度展开 */
export const deriveAnchorMonthIndices = (startDate, endDate, panelYear) => {
	const staging = anchorRangeToPeriodQuickPickStaging(startDate, endDate, panelYear);
	if (staging.months?.length) return [...staging.months].sort((a, b) => a - b);
	return null;
};

/** 从锚点区间反推季度下标（0~3） */
export const deriveAnchorQuarterIndices = (startDate, endDate, panelYear) => {
	const staging = anchorRangeToPeriodQuickPickStaging(startDate, endDate, panelYear);
	if (staging.quarters?.length) return [...staging.quarters].sort((a, b) => a - b);
	return null;
};

/** 所选月份是否恰好为若干个完整季度（可切换按季汇总） */
export const canOfferQuarterAggregation = (staging = {}) => {
	const months = staging.months ?? [];
	if (months.length < 3) return false;
	const quarters = deriveQuartersFromMonths(months);
	if (!quarters.length) return false;
	const expanded = expandQuartersToMonths(quarters);
	return months.length === expanded.size && months.every((month) => expanded.has(month));
};

const formatMonthLabels = formatMonthIndicesRangeLabel;

const formatQuarterLabels = (quarterIndices = []) =>
	quarterIndices
		.map((quarter) => STATISTICS_QUICK_PICK_QUARTERS.find((item) => item.quarter === quarter)?.label)
		.filter(Boolean)
		.join('、');

const withYearPrefix = (year, text) => {
	if (!text) return '';
	if (!Number.isFinite(year)) return text;
	if (text.startsWith(`${year}年`)) return text;
	return `${year}年 · ${text}`;
};

const monthsMatchDerivedRange = (months = [], derived = []) => {
	if (!Array.isArray(months) || !Array.isArray(derived) || !derived.length) return false;
	if (months.length !== derived.length) return false;
	const sortedMonths = [...months].sort((a, b) => a - b);
	const sortedDerived = [...derived].sort((a, b) => a - b);
	return sortedMonths.every((month, index) => month === sortedDerived[index]);
};

const isConsecutiveMonthIndices = (monthIndices = []) => {
	const sorted = [...new Set(monthIndices)].sort((a, b) => a - b);
	if (sorted.length <= 1) return true;
	return sorted.every((month, index) => index === 0 || month === sorted[index - 1] + 1);
};

const resolveDiscreteOrderCreatedMonths = (monthIndices = []) => {
	if (!monthIndices.length || isConsecutiveMonthIndices(monthIndices)) return undefined;
	return [...new Set(monthIndices)].sort((a, b) => a - b).map((month) => month + 1);
};

const serializeOrderCreatedPeriods = (periods = []) => {
	if (!Array.isArray(periods) || periods.length <= 1) return undefined;
	const normalized = periods
		.map((period) => ({
			startDate: normalizeCompareDateKey(period.startDate),
			endDate: normalizeCompareDateKey(period.endDate),
			...(period.orderCreatedMonths?.length ? { orderCreatedMonths: period.orderCreatedMonths } : {}),
		}))
		.filter((period) => period.startDate && period.endDate);
	return normalized.length > 1 ? normalized : undefined;
};

const buildOrderCreatedPeriodsPayload = (ranges = []) => {
	if (!Array.isArray(ranges) || ranges.length <= 1) return undefined;
	return ranges.map((range) => ({
		startDate: range.startDate,
		endDate: range.endDate,
		...(range.orderCreatedMonths?.length ? { orderCreatedMonths: range.orderCreatedMonths } : {}),
		...(range.periodAxis ? { periodAxis: range.periodAxis } : {}),
		...(range.months?.length ? { months: range.months } : {}),
		...(range.quarters?.length ? { quarters: range.quarters } : {}),
	}));
};

const quartersFitDerivedRange = (quarters = [], derivedQuarters = []) => {
	if (!quarters?.length || !derivedQuarters?.length) return false;
	const derivedSet = new Set(derivedQuarters);
	return quarters.every((quarter) => derivedSet.has(quarter));
};

/** 选期元数据与真实日期区间不一致时，以日期反推结果为准 */
export const reconcileAnchorPeriodMetaFromRange = ({ startDate, endDate, months = null, quarters = null, periodAxis = null, label = '' } = {}) => {
	if (!startDate || !endDate) {
		return { months, quarters, periodAxis, label };
	}

	const year = moment(startDate).year();
	if (!Number.isFinite(year)) {
		return { months, quarters, periodAxis, label };
	}

	const derivedMonths = deriveAnchorMonthIndices(startDate, endDate, year);
	const derivedQuarters = deriveAnchorQuarterIndices(startDate, endDate, year);

	// 按季汇总时 months 会刻意省略；勿因缺 months 而回落到按月
	if (periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && quarters?.length && quartersFitDerivedRange(quarters, derivedQuarters)) {
		return {
			quarters: [...new Set(quarters)].sort((a, b) => a - b),
			periodAxis: PERIOD_QUICK_PICK_AXIS.QUARTER,
			label,
		};
	}

	if (derivedMonths?.length && !monthsMatchDerivedRange(months, derivedMonths)) {
		// 跳选月份时 start/end 仅为包络区间，勿按区间反推覆盖显式 months
		if (months?.length) {
			return {
				months: [...new Set(months)].sort((a, b) => a - b),
				quarters: [],
				periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
				label: '',
			};
		}
		return {
			months: derivedMonths,
			quarters: [],
			periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
			label: '',
		};
	}

	if (periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && derivedQuarters?.length && quarters?.length && quarters.length !== derivedQuarters.length) {
		return {
			months: derivedMonths ?? months,
			quarters: derivedQuarters,
			periodAxis: PERIOD_QUICK_PICK_AXIS.QUARTER,
			label: '',
		};
	}

	return { months, quarters, periodAxis, label };
};

/** 跳选月份需单独传 API，避免包络起止日期夹带中间未选月份 */
export const resolveOrderCreatedMonthsForQuery = ({ startDate, endDate, trendAnchor } = {}) => {
	const anchor = trendAnchor;
	if (anchor?.periods?.length > 1) return undefined;
	if (!anchor?.months?.length || anchor.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER) return undefined;
	if (!startDate || !endDate) return undefined;

	const year = moment(startDate).year();
	if (!Number.isFinite(year)) return undefined;

	const derivedMonths = deriveAnchorMonthIndices(startDate, endDate, year);
	if (derivedMonths?.length && monthsMatchDerivedRange(anchor.months, derivedMonths)) {
		return undefined;
	}

	return [...new Set(anchor.months)].sort((a, b) => a - b).map((month) => month + 1);
};

/** 离散多年/多段选期：各段 OR 查询，跳过未选年份/月份 */
export const resolveOrderCreatedPeriodsForQuery = ({ trendAnchor } = {}) => serializeOrderCreatedPeriods(trendAnchor?.periods);

/** 完成日期：与下单日期共用 anchor 结构，字段为 completedDateAnchor */
export const resolveCompletedOrderMonthsForQuery = ({ startDate, endDate, completedDateAnchor } = {}) =>
	resolveOrderCreatedMonthsForQuery({ startDate, endDate, trendAnchor: completedDateAnchor });

export const resolveCompletedOrderPeriodsForQuery = ({ completedDateAnchor } = {}) => resolveOrderCreatedPeriodsForQuery({ trendAnchor: completedDateAnchor });

/** 选期按钮：优先用已保存的月/季元数据，否则从日期区间反推 */
export const resolveAnchorQuickPickButtonLabel = ({ startDate, endDate, label, months, quarters, periodAxis, periods = null } = {}) => {
	const reconciled = reconcileAnchorPeriodMetaFromRange({
		startDate,
		endDate,
		months,
		quarters,
		periodAxis,
		label,
	});
	const resolvedMonths = reconciled.months;
	const resolvedQuarters = reconciled.quarters;
	const resolvedPeriodAxis = reconciled.periodAxis;
	const resolvedLabel = reconciled.label;
	const year = moment(startDate).year();

	if (Array.isArray(periods) && periods.length > 1) {
		const periodLabels = periods
			.map((period) => {
				const periodYear = moment(period.startDate).year();
				if (!Number.isFinite(periodYear)) return '';
				const entry = entryFromSavedOrderRangePeriod(period, periodYear);
				if (!entry) return periodYear === moment().year() ? '本年' : `${periodYear}年`;
				const merged = mergeSingleYearPeriodQuickPickStagingToAnchorRange(periodYear, entry);
				return merged?.label ? withYearPrefix(periodYear, merged.label) : `${periodYear}年`;
			})
			.filter(Boolean);
		if (periodLabels.length) return periodLabels.join('；');

		const periodYears = [...new Set(periods.map((period) => moment(period.startDate).year()).filter((value) => Number.isFinite(value)))].sort((a, b) => a - b);
		if (periodYears.length > 1) {
			return formatStatisticsMultiYearRangeLabel(periodYears);
		}
	}

	if (startDate && endDate && !(Array.isArray(periods) && periods.length > 1)) {
		const spanYears = deriveYearsIncludedInRange(startDate, endDate);
		if (spanYears.length > 1) {
			const envelopeStart = normalizeCompareDateKey(buildCalendarYearRange(spanYears[0]).startDate);
			const envelopeEnd = normalizeCompareDateKey(buildCalendarYearRange(spanYears[spanYears.length - 1]).endDate);
			if (envelopeStart === normalizeCompareDateKey(startDate) && envelopeEnd === normalizeCompareDateKey(endDate)) {
				return formatStatisticsMultiYearRangeLabel(spanYears);
			}
		}
	}

	if (resolvedPeriodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && resolvedQuarters?.length) {
		if (resolvedQuarters.length === FULL_QUARTER_INDICES.length) {
			return formatFullYearPeriodAxisLabel(year, PERIOD_QUICK_PICK_AXIS.QUARTER);
		}
		return withYearPrefix(year, formatQuarterLabels(resolvedQuarters));
	}

	const trimmedLabel = resolvedLabel?.trim() || label?.trim();
	if (trimmedLabel) {
		return withYearPrefix(year, trimmedLabel);
	}

	if (startDate && endDate && Number.isFinite(year) && matchCalendarYear(startDate, endDate, year)) {
		return year === moment().year() ? '本年' : `${year}年`;
	}

	if (resolvedPeriodAxis === PERIOD_QUICK_PICK_AXIS.MONTH && resolvedMonths?.length) {
		return withYearPrefix(year, formatMonthLabels(resolvedMonths));
	}

	if (startDate && endDate && Number.isFinite(year)) {
		const staging = anchorRangeToPeriodQuickPickStaging(startDate, endDate, year);
		if (isFullYearPeriodSelection(staging.months ?? [], staging.quarters ?? [])) {
			return formatFullYearPeriodAxisLabel(year, staging.periodAxis ?? PERIOD_QUICK_PICK_AXIS.MONTH);
		}
		if (staging.months?.length > 1) {
			if (resolvedPeriodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && canOfferQuarterAggregation(staging) && staging.quarters?.length) {
				return withYearPrefix(year, formatQuarterLabels(staging.quarters));
			}
			return withYearPrefix(year, formatMonthLabels(staging.months));
		}
	}

	return resolveStatisticsPeriodDisplayLabel(startDate, endDate) || '';
};

/** 单年内多选月/季 → 合并为一个连续锚点区间 */
const mergeSingleYearPeriodQuickPickStagingToAnchorRange = (year, staging = {}) => {
	if (!Number.isFinite(Number(year))) return null;

	const normalized = normalizePeriodQuickPickStaging(staging);
	const sortedMonths = normalized.months ?? [];
	const sortedQuarters = normalized.quarters ?? [];
	const prefersQuarterAxis = normalized.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER;
	const useQuarterAxis = prefersQuarterAxis && canOfferQuarterAggregation(normalized) && sortedQuarters.length > 0;

	if (sortedMonths.length > 0) {
		if (isFullYearPeriodSelection(sortedMonths, sortedQuarters)) {
			const fullYearRange = buildCalendarYearRange(year);
			const actualMonths = deriveMonthIndicesSpanningRange(fullYearRange.startDate, fullYearRange.endDate);
			const isCompleteCalendarYear = actualMonths.length === FULL_YEAR_MONTH_INDICES.length;
			const useQuarterAxisForFullYear = isCompleteCalendarYear && prefersQuarterAxis && canOfferQuarterAggregation(normalized);
			const fullYearAxis = useQuarterAxisForFullYear ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH;
			const merged = {
				startDate: fullYearRange.startDate,
				endDate: fullYearRange.endDate,
				label: isCompleteCalendarYear
					? formatFullYearPeriodAxisLabel(year, fullYearAxis)
					: year === moment().year()
						? '本年'
						: withYearPrefix(year, formatMonthLabels(actualMonths)),
				anchorYear: year,
				periodAxis: fullYearAxis,
			};
			if (useQuarterAxisForFullYear) {
				merged.quarters = FULL_QUARTER_INDICES;
			} else {
				merged.months = actualMonths;
			}
			return merged;
		}

		const startRange = buildCalendarMonthRange(year, sortedMonths[0]);
		const endRange = buildCalendarMonthRange(year, sortedMonths[sortedMonths.length - 1]);
		const merged = {
			startDate: startRange.startDate,
			endDate: endRange.endDate,
			label: useQuarterAxis ? formatQuarterLabels(sortedQuarters) : formatMonthLabels(sortedMonths),
			anchorYear: year,
			periodAxis: useQuarterAxis ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH,
		};

		if (useQuarterAxis) {
			merged.quarters = sortedQuarters;
		} else {
			merged.months = sortedMonths;
			const discreteMonths = resolveDiscreteOrderCreatedMonths(sortedMonths);
			if (discreteMonths) merged.orderCreatedMonths = discreteMonths;
		}
		return merged;
	}

	return null;
};

const formatOrderRangeYearLabel = (year, staging = {}) => {
	if (!Number.isFinite(Number(year))) return '';
	const entry = staging.yearPeriods?.[Number(year)];
	if (!entry?.months?.length) {
		return year === moment().year() ? '本年' : `${year}年`;
	}
	const merged = mergeSingleYearPeriodQuickPickStagingToAnchorRange(year, entry);
	return merged?.label ? withYearPrefix(year, merged.label) : `${year}年`;
};

/** 下单/完成日期：按 years + yearPeriods 合并为多段 OR 查询 */
export const mergeOrderRangePeriodQuickPickStaging = (staging = {}) => {
	const normalized = normalizePeriodQuickPickStaging(staging);
	const selectedYears = (normalized.years ?? []).filter(Number.isFinite);
	if (!selectedYears.length) return null;

	const ranges = selectedYears
		.map((year) => {
			const entry = normalized.yearPeriods?.[Number(year)];
			if (entry?.months?.length) {
				return mergeSingleYearPeriodQuickPickStagingToAnchorRange(year, entry);
			}
			const fullYearRange = buildCalendarYearRange(year);
			return {
				...fullYearRange,
				label: year === moment().year() ? '本年' : `${year}年`,
				anchorYear: year,
			};
		})
		.filter(Boolean);

	if (!ranges.length) return null;

	if (ranges.length === 1) {
		const single = ranges[0];
		return {
			startDate: single.startDate,
			endDate: single.endDate,
			label: formatOrderRangeYearLabel(selectedYears[0], normalized),
			years: selectedYears,
			yearPeriods: normalized.yearPeriods ?? {},
			...(single.months?.length ? { months: single.months } : {}),
			...(single.quarters?.length ? { quarters: single.quarters } : {}),
			...(single.periodAxis ? { periodAxis: single.periodAxis } : {}),
			...(single.orderCreatedMonths?.length ? { orderCreatedMonths: single.orderCreatedMonths } : {}),
		};
	}

	const periods = buildOrderCreatedPeriodsPayload(ranges);
	const startDate = ranges.reduce((min, range) => {
		const current = normalizeCompareDateKey(range.startDate);
		const previous = normalizeCompareDateKey(min);
		return current && previous && current < previous ? range.startDate : min;
	}, ranges[0].startDate);
	const endDate = ranges.reduce((max, range) => {
		const current = normalizeCompareDateKey(range.endDate);
		const previous = normalizeCompareDateKey(max);
		return current && previous && current > previous ? range.endDate : max;
	}, ranges[0].endDate);

	return {
		startDate,
		endDate,
		label: selectedYears.map((year) => formatOrderRangeYearLabel(year, normalized)).join('；'),
		years: selectedYears,
		yearPeriods: normalized.yearPeriods ?? {},
		...(periods ? { periods, chartAxisFromOrderRange: 'month' } : {}),
	};
};

/** 同一年内多选月/季 → 合并为一个连续锚点区间；多年则按年合并后取包络 */
export const mergePeriodQuickPickStagingToAnchorRange = (panelYear, staging = {}) => {
	const normalized = normalizePeriodQuickPickStaging(staging);
	const selectedYears = (normalized.years?.length ? normalized.years : [Number(panelYear)]).filter(Number.isFinite);
	if (!selectedYears.length) return null;

	if (selectedYears.length === 1) {
		const singleYear = selectedYears[0];
		return mergeSingleYearPeriodQuickPickStagingToAnchorRange(singleYear, normalized) ?? (normalized.years?.includes(singleYear) ? buildCalendarYearRange(singleYear) : null);
	}

	const ranges = selectedYears
		.map((year) => {
			const yearStaging = year === panelYear ? normalized : { ...normalized, months: [], quarters: [], periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH };
			return mergeSingleYearPeriodQuickPickStagingToAnchorRange(year, yearStaging) ?? buildCalendarYearRange(year);
		})
		.filter(Boolean);

	if (!ranges.length) return null;

	const periods = buildOrderCreatedPeriodsPayload(ranges);
	const startDate = ranges.reduce((min, range) => {
		const current = normalizeCompareDateKey(range.startDate);
		const previous = normalizeCompareDateKey(min);
		return current && previous && current < previous ? range.startDate : min;
	}, ranges[0].startDate);
	const endDate = ranges.reduce((max, range) => {
		const current = normalizeCompareDateKey(range.endDate);
		const previous = normalizeCompareDateKey(max);
		return current && previous && current > previous ? range.endDate : max;
	}, ranges[0].endDate);
	const panelYearRange = mergeSingleYearPeriodQuickPickStagingToAnchorRange(panelYear, normalized);

	return {
		startDate,
		endDate,
		label: formatMultiYearQuickPickLabel(selectedYears, panelYear, panelYearRange),
		years: selectedYears,
		...(periods ? { periods } : {}),
		...(panelYearRange?.months?.length ? { months: panelYearRange.months } : {}),
		...(panelYearRange?.quarters?.length ? { quarters: panelYearRange.quarters } : {}),
		...(panelYearRange?.periodAxis ? { periodAxis: panelYearRange.periodAxis } : {}),
	};
};

export const togglePeriodQuickPickMonth = (staging, monthIndex) => {
	const months = new Set(staging.months ?? []);
	if (months.has(monthIndex)) months.delete(monthIndex);
	else months.add(monthIndex);
	return normalizePeriodQuickPickStaging({
		...staging,
		months: [...months],
		periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
	});
};

export const togglePeriodQuickPickQuarter = (staging, quarterIndex) => {
	const months = new Set(staging.months ?? []);
	const quarterMonths = monthsForQuarter(quarterIndex);
	const allSelected = quarterMonths.every((month) => months.has(month));
	for (const month of quarterMonths) {
		if (allSelected) months.delete(month);
		else months.add(month);
	}
	const nextMonths = [...months];
	const nextQuarters = deriveQuartersFromMonths(nextMonths);
	// 季度 chip 操作：新增/取消均保持按季粒度，只要剩余月份仍构成完整季度
	const useQuarterAxis =
		nextQuarters.length > 0 && canOfferQuarterAggregation({ months: nextMonths, quarters: nextQuarters });
	return normalizePeriodQuickPickStaging({
		...staging,
		months: nextMonths,
		periodAxis: useQuarterAxis ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH,
	});
};

export const togglePeriodQuickPickYear = (staging, year) => {
	const years = new Set(staging.years ?? []);
	if (years.has(year)) years.delete(year);
	else years.add(year);
	const nextYears = [...years].sort((a, b) => a - b);
	if (nextYears.length > TREND_MONTHLY_YEARS_MAX) return staging;
	return { ...staging, years: nextYears };
};

/** 整年 chip 高亮：须 12 个月齐选，且 panelYear 在已选年份中（无已选年份时仅看月份） */
export const isPeriodQuickPickPanelFullYearActive = (staging = {}, panelYear) => {
	const months = staging.months ?? [];
	if (months.length !== FULL_YEAR_MONTH_INDICES.length) return false;
	if (!Number.isFinite(Number(panelYear))) return false;
	const years = staging.years ?? [];
	if (!years.length) return true;
	return years.includes(Number(panelYear));
};

export const toggleOrderRangePeriodQuickPickMonth = (staging, editYear, monthIndex) => {
	const view = getOrderRangeYearPeriodView(staging, editYear);
	const next = togglePeriodQuickPickMonth(view, monthIndex);
	return persistOrderRangeYearPeriodView(staging, editYear, next);
};

export const toggleOrderRangePeriodQuickPickQuarter = (staging, editYear, quarterIndex) => {
	const view = getOrderRangeYearPeriodView(staging, editYear);
	const next = togglePeriodQuickPickQuarter(view, quarterIndex);
	return persistOrderRangeYearPeriodView(staging, editYear, next);
};

export const setOrderRangeEditYearPeriodAxis = (staging, editYear, periodAxis) => {
	const view = getOrderRangeYearPeriodView(staging, editYear);
	const next = normalizePeriodQuickPickStaging({ ...view, periodAxis });
	return persistOrderRangeYearPeriodView(staging, editYear, next);
};

/** 折线锚点选期：年份可多选，月/季仅作用于 panelYear */
export const togglePeriodQuickPickLineAnchorYears = (staging, year, panelYear) => {
	const years = new Set(staging.years ?? []);
	const wasSelected = years.has(year);
	if (wasSelected) years.delete(year);
	else years.add(year);
	const next = {
		...staging,
		years: [...years].sort((a, b) => a - b),
	};
	if (wasSelected && Number(year) === Number(panelYear)) {
		next.months = [];
		next.quarters = [];
	}
	return normalizePeriodQuickPickStaging(next);
};

/** 下单/完成日期选期：年份可多选（无 5 年上限）；选中即整年，取消则移除该年细选 */
export const togglePeriodQuickPickOrderYears = (staging, year) => {
	const years = new Set(staging.years ?? []);
	const wasSelected = years.has(year);
	if (wasSelected) years.delete(year);
	else years.add(year);
	const nextYears = [...years].sort((a, b) => a - b);
	const yearPeriods = { ...(staging.yearPeriods ?? {}) };
	if (wasSelected) {
		delete yearPeriods[Number(year)];
	}
	return normalizePeriodQuickPickStaging({
		...staging,
		years: nextYears,
		yearPeriods,
		months: [],
		quarters: [],
		periodAxis: PERIOD_QUICK_PICK_AXIS.MONTH,
	});
};

export const formatPeriodQuickPickStagingSummary = (staging, panelYear, purpose) => {
	if (purpose === 'line-year-months') {
		const years = staging.years ?? [];
		if (!years.length) return '选年份';
		return years.map((year) => `${year}年`).join('、');
	}

	if (purpose === 'order-range') {
		const years = staging.years ?? [];
		if (!years.length) return '选年份';
		if (years.length === 1) {
			return formatOrderRangeYearLabel(years[0], staging);
		}
		return years.map((year) => formatOrderRangeYearLabel(year, staging)).join('；');
	}

	const selectedYears = staging.years ?? [];
	if (selectedYears.length > 1) {
		const panelYearRange = mergeSingleYearPeriodQuickPickStagingToAnchorRange(panelYear, staging);
		return formatMultiYearQuickPickLabel(selectedYears, panelYear, panelYearRange) || '已选未确定';
	}

	const merged = mergePeriodQuickPickStagingToAnchorRange(panelYear, staging);
	if (merged?.label) return merged.label;
	if ((staging.months?.length ?? 0) > 0) {
		if (isFullYearPeriodSelection(staging.months ?? [], staging.quarters ?? [])) {
			return formatFullYearPeriodAxisLabel(panelYear, staging.periodAxis ?? PERIOD_QUICK_PICK_AXIS.MONTH);
		}
		if (staging.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && canOfferQuarterAggregation(staging)) {
			return formatQuarterLabels(staging.quarters ?? []);
		}
		return formatMonthLabels(staging.months ?? []);
	}
	if ((staging.months?.length ?? 0) + (staging.quarters?.length ?? 0) > 0) return '已选未确定';
	return '';
};
